import React, { useState } from "react";
import { Button } from "../../../design/button";
import { useVisit } from "../../../contexts/VisitContext";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  DialogDescription,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import { Field, Fieldset, FieldGroup, Label } from "../../../design/fieldset";
import { Text, Strong } from "../../../design/text";
import {
  ExclamationTriangleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

interface Props {
  visitId: string;
  hasEvents?: boolean;
  hasReports?: boolean;
}

const DeleteVisitButton: React.FC<Props> = ({
  visitId,
  hasEvents = false,
  hasReports = false,
}) => {
  const { deleteVisitById } = useVisit();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirmationWord = "DELETE";
  const isConfirmed = confirmText === confirmationWord;

  const handleDelete = async () => {
    if (!isConfirmed) return;

    setIsDeleting(true);
    setError(null);

    try {
      await deleteVisitById(visitId);
      setIsOpen(false);
      navigate("/visits");
    } catch (err) {
      setError("Failed to delete visit. Please try again.");
      console.error("Delete visit error:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (isDeleting) return;
    setIsOpen(false);
    setConfirmText("");
    setError(null);
  };

  return (
    <>
      <Button type="button" color="red" onClick={() => setIsOpen(true)}>
        Delete
      </Button>

      <Dialog open={isOpen} onClose={handleClose} size="lg">
        <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
          <ExclamationTriangleIcon className="h-6 w-6" />
          Delete Visit Permanently
        </DialogTitle>
        <DialogDescription>
          This action is irreversible. Please review the following information
          carefully.
        </DialogDescription>

        <DialogBody className="space-y-6">
          {/* Warning Banner */}
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
            <div className="flex gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
              <div className="space-y-2">
                <Strong className="text-red-800 dark:text-red-200">
                  Warning: This action cannot be undone
                </Strong>
                <Text className="text-sm text-red-700 dark:text-red-300">
                  Deleting this visit will permanently remove it from the
                  system. This may affect billing, reporting, and compliance
                  records.
                </Text>
              </div>
            </div>
          </div>

          {/* What will be deleted */}
          <div className="space-y-3">
            <Text className="font-medium text-zinc-900 dark:text-zinc-100">
              The following will be permanently deleted:
            </Text>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <TrashIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-zinc-500" />
                <Text className="text-zinc-700 dark:text-zinc-300">
                  The visit record and all scheduling information
                </Text>
              </li>
              {hasEvents && (
                <li className="flex items-start gap-2">
                  <TrashIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-500" />
                  <Text className="text-red-700 dark:text-red-300">
                    <Strong>All visit events</Strong>, including check-ins,
                    photos, and notes
                  </Text>
                </li>
              )}
              {hasReports && (
                <li className="flex items-start gap-2">
                  <TrashIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-500" />
                  <Text className="text-red-700 dark:text-red-300">
                    <Strong>Associated reports</Strong> will be orphaned and may
                    become inaccessible
                  </Text>
                </li>
              )}
              <li className="flex items-start gap-2">
                <TrashIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-zinc-500" />
                <Text className="text-zinc-700 dark:text-zinc-300">
                  Parent and supervisor notifications related to this visit
                </Text>
              </li>
            </ul>
          </div>

          {/* Additional Disclosures */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
            <Text className="text-sm text-amber-800 dark:text-amber-200">
              <Strong>Compliance Notice:</Strong> If this visit has been
              invoiced or is part of a court-ordered visitation schedule,
              deleting it may affect your records. Consider cancelling the visit
              instead to preserve an audit trail.
            </Text>
          </div>

          {/* Confirmation Input */}
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>
                  Type <Strong className="text-red-600">{confirmationWord}</Strong>{" "}
                  to confirm deletion
                </Label>
                <Input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  placeholder={`Type ${confirmationWord} to confirm`}
                  disabled={isDeleting}
                  className={
                    confirmText && !isConfirmed
                      ? "border-red-300 dark:border-red-700"
                      : ""
                  }
                />
                {confirmText && !isConfirmed && (
                  <Text className="mt-1 text-xs text-red-600 dark:text-red-400">
                    Please type "{confirmationWord}" exactly to enable deletion
                  </Text>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>

          {/* Error Display */}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
              <Text className="text-sm text-red-700 dark:text-red-300">
                {error}
              </Text>
            </div>
          )}
        </DialogBody>

        <DialogActions>
          <Button plain onClick={handleClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="red"
            disabled={!isConfirmed || isDeleting}
            onClick={handleDelete}
          >
            {isDeleting ? "Deleting..." : "Delete Visit Permanently"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeleteVisitButton;
